import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePortalSlug } from "@/contexts/PortalDomainContext";
import api from "@/lib/api";
import { loadTenantFont, resolveLogoUrl } from "@/lib/branding";
import { useAuth } from "@/contexts/AuthContext";
import AuthPanel from "@/components/AuthPanel";
import usePageTitle from "@/lib/usePageTitle";
import { Recycle } from "@phosphor-icons/react";

export default function PortalLogin() {
  const slug = usePortalSlug();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [tenant, setTenant] = useState(null);
  usePageTitle("Anmelden", tenant?.name);

  useEffect(() => {
    api.get(`/tenants/public/${slug}`).then((r) => {
      setTenant(r.data);
      const b = r.data.branding || {};
      document.documentElement.style.setProperty("--tenant-primary", b.primary_color || "#2563EB");
      document.documentElement.style.setProperty("--tenant-secondary", b.secondary_color || "#0EA5E9");
      document.documentElement.style.setProperty("--tenant-font", `"${b.font_family || "Outfit"}", sans-serif`);
      loadTenantFont(b.font_family);
    }).catch(() => {});
  }, [slug]);

  const onSuccess = async () => {
    const authUser = await checkAuth();
    const role = authUser?.role;
    if (role === "platform_admin") {
      navigate("/admin", { replace: true });
    } else if (role === "tenant_owner" || role === "tenant_staff") {
      navigate("/app", { replace: true });
    } else if (role === "tenant_driver") {
      navigate("/driver", { replace: true });
    } else {
      navigate(`/portal/${slug}/account`, { replace: true });
    }
  };

  if (!tenant) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;
  const b = tenant.branding || {};
  const gradient = `linear-gradient(135deg, ${b.primary_color || "#2563EB"} 0%, ${b.secondary_color || "#0EA5E9"} 100%)`;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" style={{ background: "var(--tenant-surface, #fafafa)", fontFamily: "var(--tenant-font)" }}>
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-15 blur-3xl" style={{ background: gradient }} />
      <div className="w-full max-w-md relative">
        <div className="bg-white rounded-2xl p-10 shadow-xl border border-black/5">
          <div className="flex flex-col items-center mb-5">
            {b.logo_url ? (
              <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-14 mb-3" />
            ) : (
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-3" style={{ background: b.primary_color }}>
                <Recycle size={28} weight="bold" className="text-white" />
              </div>
            )}
            <h2 className="font-heading text-xl font-bold tracking-tight">{tenant.name}</h2>
          </div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-center mb-1">Anmelden</h1>
          <p className="text-sm text-zinc-500 mb-6 text-center">Kunden, Mitarbeiter und Fahrer melden sich hier an. Sie werden automatisch zu Ihrem Bereich weitergeleitet.</p>
          <AuthPanel tenantSlug={slug} onSuccess={onSuccess} primaryGradient={gradient} googleRedirectPath={`/portal/${slug}/account`} />
        </div>
        <p className="text-center text-xs text-zinc-500 mt-6">
          <Link to={`/portal/${slug}`} className="hover:underline">← zurück zur {tenant.name} Startseite</Link>
        </p>
      </div>
    </div>
  );
}
